import { Switch, SwitchProps, Box } from '@mui/material';
import { styled, Theme } from '@mui/material/styles';
import { ReactNode } from 'react';

interface AntSwitchProps extends SwitchProps {
    thumbIcon?: ReactNode;
    checkedThumbIcon?: ReactNode;
    width?: number;
    height?: number;
}

const DEFAULT_SWITCH_WIDTH = 44;
const DEFAULT_SWITCH_HEIGHT = 26;
const THUMB_PADDING = 3;

interface SwitchSizeProps {
    switchWidth: number;
    switchHeight: number;
}

const StyledAntSwitch = styled(Switch, {
    shouldForwardProp: (prop) => prop !== 'switchWidth' && prop !== 'switchHeight',
})<SwitchSizeProps>(({ theme, switchWidth, switchHeight }: { theme: Theme } & SwitchSizeProps) => {
    const thumbSize = switchHeight - THUMB_PADDING * 2;
    const translateX = switchWidth - thumbSize - THUMB_PADDING * 2;

    return {
        width: switchWidth,
        height: switchHeight,
        padding: 0,
        display: 'flex',
        '& .MuiSwitch-switchBase': {
            padding: THUMB_PADDING,
            '&.Mui-checked': {
                transform: `translateX(${translateX}px)`,
                color: '#fff',
                '& + .MuiSwitch-track': {
                    opacity: 1,
                    backgroundColor: theme.palette.primary.main,
                },
            },
        },
        '& .MuiSwitch-thumb': {
            boxShadow: '0 2px 4px 0 rgb(0 35 11 / 20%)',
            width: thumbSize,
            height: thumbSize,
            borderRadius: thumbSize / 2,
            transition: theme.transitions.create(['width'], {
                duration: 200,
            }),
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
        },
        '& .MuiSwitch-track': {
            borderRadius: switchHeight / 2,
            opacity: 1,
            backgroundColor: theme.palette.background.neutral,
            boxSizing: 'border-box',
            ...theme.applyStyles('dark', {
                backgroundColor: 'rgba(255,255,255,.35)',
            }),
        },
    };
});

const IconThumb = styled(Box, {
    shouldForwardProp: (prop) => prop !== 'size',
})<{ size: number }>(({ theme, size }: { theme: Theme; size: number }) => ({
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    width: size,
    height: size,
    backgroundColor: theme.palette.common.white,
    borderRadius: size / 2,
}));

const AntSwitch = ({
    thumbIcon,
    checkedThumbIcon,
    width = DEFAULT_SWITCH_WIDTH,
    height = DEFAULT_SWITCH_HEIGHT,
    ...props
}: AntSwitchProps) => {
    const thumbSize = height - THUMB_PADDING * 2;

    if (thumbIcon) {
        return (
            <StyledAntSwitch
                {...props}
                switchWidth={width}
                switchHeight={height}
                icon={<IconThumb size={thumbSize}>{thumbIcon}</IconThumb>}
                checkedIcon={
                    <IconThumb size={thumbSize}>{checkedThumbIcon || thumbIcon}</IconThumb>
                }
            />
        );
    }

    return <StyledAntSwitch {...props} switchWidth={width} switchHeight={height} />;
};

export default AntSwitch;
